import type { AnyAction } from "@reduxjs/toolkit";
import type { ToastCTAProps } from "@replo/design-system/components/alert/Toast";
import type { ErrorDetails } from "schemas/errors";
import type { ErrorUserFacingDetails } from "schemas/generated/errors";

import * as React from "react";

import { ConnectShopifyIntegrationCta } from "@editor/components/editor/page/ConnectShopifyCallout";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { breadcrumb } from "@editor/infra/nonfatal";
import { handleErrorDetails } from "@editor/reducers/modals-reducer";
import { userUtils } from "@editor/trpc/user";

import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/unstable-core-do-not-import";
import { generatePath } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  CUSTOM_ERROR_KEY_TO_PROPS,
  DEFAULT_ERROR_MESSAGE_CONFIG,
  errorUserFacingDetailsSchema,
} from "schemas/errors";
import z from "zod";

import { docs } from "./docs";
import { routes } from "./router";

function ToastConnectShopifyCta() {
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  return (
    <ConnectShopifyIntegrationCta
      cookieValue={{
        type: "noShopifyErrorModal",
        projectId,
        workspaceId,
      }}
    />
  );
}

function NavigateButton({
  location,
  title,
}: {
  title: string;
  location: (opts: {
    projectId: string | undefined;
    workspaceId: string | undefined;
  }) => string;
}) {
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const to = location({ projectId, workspaceId: workspaceId ?? undefined });
  return (
    <Button variant="primary" size="base" to={to}>
      {title}
    </Button>
  );
}

const errorUserFacingDetailsToErrorDetails = (
  errorUserFacingDetails: ErrorUserFacingDetails,
):
  | {
      errorDetails: ErrorDetails;
      type: "fullPageModal" | "toast";
      errorKey?: string;
      endpointName?: string;
    }
  | {
      type: "shopifyStorePassword";
      errorKey: "passwordRequired" | "wrongPassword";
    }
  | { type: "none"; errorKey?: string; endpointName?: string }
  | null => {
  if (errorUserFacingDetails.type === "uncommon_none") {
    return null;
  }
  if (errorUserFacingDetails.type === "shopifyStorePassword") {
    return {
      type: "shopifyStorePassword",
      errorKey: errorUserFacingDetails.key as
        | "passwordRequired"
        | "wrongPassword",
    };
  }
  const errorDetails: ErrorDetails = {
    header: errorUserFacingDetails.message,
    message: errorUserFacingDetails.detail ?? [],
    isRecoverable: errorUserFacingDetails.isRecoverable ?? false,
    isFullPage: errorUserFacingDetails.type === "fullPageModal",
    usage: errorUserFacingDetails.usage,
  };

  if (errorUserFacingDetails.callToAction) {
    if (typeof errorUserFacingDetails.callToAction === "object") {
      errorDetails.callToAction = {
        type: "custom",
        callToActionType: errorUserFacingDetails.callToAction,
      };
    } else {
      errorDetails.callToAction = exhaustiveSwitch({
        type: errorUserFacingDetails.callToAction,
      })({
        installApp: {
          type: "installApp",
        },
        reload: {
          type: "callback",
          name: "Refresh",
          onClick: () => window.location.reload(),
        },
        closeModal: {
          type: "closeModal",
          name: "Close",
        },
        navigateToProjectEditor: {
          type: "custom",
          callToActionType: errorUserFacingDetails.callToAction,
        },
        goToHomepage: {
          type: "callback",
          name: "Go To Home",
          onClick: () => window.location.replace("/home"),
        },
      });
    }
  }
  return {
    type:
      errorUserFacingDetails.type === "fullPageModal"
        ? "fullPageModal"
        : "toast",
    errorDetails,
    errorKey: errorUserFacingDetails.key,
  };
};

export const getToastCtaForErrorDetails = (
  errorDetails: ErrorDetails,
): ToastCTAProps | null => {
  if (!errorDetails.callToAction) {
    return null;
  }
  return exhaustiveSwitch(errorDetails.callToAction)({
    installApp: (): ToastCTAProps => ({
      cta: <ToastConnectShopifyCta />,
    }),
    callback: (data) => ({
      cta: (
        <Button variant="primary" size="base" onClick={data.onClick}>
          {data.name}
        </Button>
      ),
    }),
    link: (data) => ({
      cta: data.name,
      ctaHref: data.to,
      ctaTarget: data.target,
    }),
    closeModal: () => null,
    navigate: (data) => {
      return exhaustiveSwitch(data.location)({
        integrationHub: (): ToastCTAProps => ({
          cta: (
            <NavigateButton
              title="View Integrations"
              location={({ projectId }) =>
                generatePath(routes.shops.settings.shops.integrations.home, {
                  projectId,
                })
              }
            />
          ),
        }),
        home: () => ({
          cta: "Go Home",
          ctaHref: routes.home.root,
        }),
        workspace: ({ workspaceName }) => ({
          cta: (
            <NavigateButton
              title={`Go to "${workspaceName}"`}
              location={({ workspaceId }) =>
                generatePath(routes.workspace.settings.general, { workspaceId })
              }
            />
          ),
        }),
      });
    },
    custom: (data): ToastCTAProps | null => {
      if (!data.callToActionType) {
        return null;
      }
      return exhaustiveSwitch(data.callToActionType)({
        closeModal: () => null,
        navigateToLink: (data): ToastCTAProps => {
          return exhaustiveSwitch(data.link)({
            "errors.chunkingError": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.chunkingError,
            }),
            "errors.wrongJsonExtension": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.wrongJsonExtension,
            }),
            "errors.indexBackupError": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.indexBackupError,
            }),
            "errors.shopifyIntegrationAlreadyAssigned": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.shopifyIntegrationAlreadyAssigned,
            }),
            "errors.shopifyIntegrationMissingAccessScopes": (data) => ({
              cta: "Authorize",
              ctaHref: data.url,
            }),
            "errors.projectConnectedToBillingIntegrationForShopifyWorkspace":
              () => ({
                cta: "Learn more",
                ctaHref:
                  docs.errors
                    .projectConnectedToBillingIntegrationForShopifyWorkspace,
              }),
            "errors.themeAssetSizeLimitExceeded": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.themeAssetSizeLimitExceeded,
            }),
            "errors.themeTotalSizeLimitExceeded": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.themeTotalSizeLimitExceeded,
            }),
            "settings.billing": () => ({
              cta: "Upgrade Plan",
              ctaHref: "/billing",
            }),
            customLink: (data) => ({
              cta: "Learn more",
              ctaHref: data.url,
            }),
          });
        },
        reload: () => ({
          cta: "Refresh",
          ctaOnClick: () => window.location.reload(),
        }),
        installApp: (): ToastCTAProps => ({
          cta: <ToastConnectShopifyCta />,
        }),
        goToHomepage: () => ({
          cta: "Go Home",
          ctaHref: routes.home.root,
        }),
        navigateToProjectEditor: () => ({
          cta: (
            <NavigateButton
              title="Go back to project"
              location={({ projectId }) =>
                generatePath(routes.editor.project, { projectId })
              }
            />
          ),
        }),
      });
    },
  });
};

/**
 * Handle the given error from TRPC (this triggers the standard toast/modal
 * processing).
 *
 * Note (Noah, 2024-12-23): The type here is hard to express, since TRPC
 * actually generates a TRPCErrorLike generic, but we only need the data here,
 * so this type is { data: unknown }
 */
export function handleTRPCClientError(error: {
  data?: { path: string } | null;
  meta?: { response?: { url?: string } } | null;
  shape?: { code: number; message?: string } | null;
}) {
  const result = z
    .object({ userFacingDetails: errorUserFacingDetailsSchema })
    .safeParse(error.data);
  const errorId = error.shape?.message?.match(/Error id ([\da-f-]+)/)?.[1];
  const details = getErrorDetails({
    type: "trpcError",
    userFacingDetails: result.success
      ? result.data.userFacingDetails
      : undefined,
    code: error.shape?.code,
    path: error.data?.path,
    url: error.meta?.response?.url,
    errorId,
    isSuperuser: userUtils.get.getData({})?.isSuperuser ?? false,
  });
  if (details && details.type === "toast") {
    toast({
      type: "error",
      header: details.errorDetails.header,
      message: details.errorDetails.message,
      ...getToastCtaForErrorDetails(details.errorDetails),
    });
  }
  if (details && details.type === "fullPageModal") {
    // Note (Noah, 2024-05-27): We need to import the store dynamically to avoid a circular dependency,
    // since core-reducer imports this file in order to invalidate queries
    void import("@editor/store").then((module) => {
      module.store.dispatch(handleErrorDetails({ details }));
    });
  }
}

export const getErrorDetails = (
  config:
    | {
        type: "rsaaOrRtkQueryAction";
        action: AnyAction;
      }
    | {
        type: "trpcError";
        userFacingDetails?: ErrorUserFacingDetails;
        code?: number;
        path?: string;
        url?: string;
        errorId?: string;
        isSuperuser?: boolean;
      },
):
  | {
      errorDetails: ErrorDetails;
      type: "fullPageModal" | "toast";
      errorKey?: string;
      endpointName?: string;
    }
  | {
      type: "shopifyStorePassword";
      errorKey: "passwordRequired" | "wrongPassword";
    }
  | { type: "none"; errorKey?: string; endpointName?: string }
  | null => {
  if (
    config.type === "rsaaOrRtkQueryAction" &&
    !Boolean(config.action.type?.endsWith("_ERROR")) &&
    !Boolean(config.action.type?.endsWith("/rejected"))
  ) {
    return null;
  }

  // Note (Evan, 2024-04-26): Don't throw a toast when AI requests are aborted
  // (this can be user-triggered)
  if (
    config.type === "rsaaOrRtkQueryAction" &&
    config.action.meta?.aborted === true &&
    (config.action.meta?.arg?.endpointName === "aiAltText" ||
      config.action.meta?.arg?.endpointName === "aiTextV2" ||
      config.action.meta?.arg?.endpointName === "aiMobileResponsive" ||
      config.action.meta?.arg?.endpointName === "aiTemplate")
  ) {
    return null;
  }

  breadcrumb("Network request returned failure/rejected", "error", {
    type:
      config.type === "rsaaOrRtkQueryAction"
        ? config.action.type
        : config.userFacingDetails?.key,
  });

  if (config.type === "trpcError") {
    let errorUserFacingDetails = config.userFacingDetails;
    if (
      !errorUserFacingDetails &&
      config.code === TRPC_ERROR_CODES_BY_KEY.TOO_MANY_REQUESTS
    ) {
      errorUserFacingDetails = {
        type: "toast",
        key: "unknown",
        message: "Too many requests",
        detail:
          "You've made too many requests in a short time period. Please try again later, or reach out to support@replo.app for help.",
      };
    }

    if (!errorUserFacingDetails) {
      const errorDisplay = (() => {
        if (config.url) {
          return `${new URL(config.url).pathname.replace("/api/v1/trpc/", "").replaceAll("/", ".")}`;
        }
        if (config.path) {
          return `Procedure ${config.path}`;
        }
        return "Procedure";
      })();

      errorUserFacingDetails = {
        type: "toast",
        key: "unknown",
        message: "Unknown error",
        detail: config.isSuperuser
          ? `An unknown error has occurred. ${errorDisplay} ${config.errorId}`
          : "An unknown error has occurred. If this error persists, please reach out to support@replo.app for help.",
      };
    }
    const details = errorUserFacingDetailsToErrorDetails(
      errorUserFacingDetails,
    );
    if (
      details &&
      details.type === "toast" &&
      !details.errorDetails.callToAction &&
      config.isSuperuser
    ) {
      if (isLocalhost()) {
        // Note (Noah, 2025-10-03): We want to eliminate unknown errors. If it's local dev, make it a huge
        // full page modal so the experience is so disruptive that people will report it
        details.type = "fullPageModal";
      }
      details.errorDetails.callToAction = {
        type: "callback",
        name: "Copy For Slack",
        onClick: () => {
          navigator.clipboard.writeText(
            `Error ID: ${config.errorId ?? ""}\nSentry: https://replo-hq.sentry.io/issues/?project=4504539522007040&query=is%3Aunresolved%20errorId%3A${config.errorId ?? ""}&referrer=issue-list&statsPeriod=7d}`,
          );
          toast({
            type: "success",
            header: "Copied",
            message:
              "Paste the error report into Slack and engineering will look at it",
          });
        },
      };
    }
    return details;
  }

  // Note (Evan, 2024-01-19): If the error contains userFacingDetails, just use those
  const errorUserFacingDetails = errorUserFacingDetailsSchema.safeParse(
    config.action?.payload?.data ?? config.action?.payload?.response,
  );
  if (errorUserFacingDetails.success) {
    return errorUserFacingDetailsToErrorDetails(errorUserFacingDetails.data);
  }

  const errorKey =
    config.action?.payload?.response?.key ?? config.action?.payload?.data?.key;
  const messageFromServer =
    config.action?.payload?.response?.message ??
    config.action?.payload?.data?.message;

  // Note (Evan, 2024-01-19): Otherwise, look in the custom error key map
  let errorDetails: ErrorDetails | null = null;
  if (errorKey) {
    errorDetails = CUSTOM_ERROR_KEY_TO_PROPS[
      errorKey as keyof typeof CUSTOM_ERROR_KEY_TO_PROPS
    ]?.({
      type: errorKey,
      serverMessage: messageFromServer,
      endpoint: config.action.meta?.arg?.endpointName,
      // Note (Noah, 2024-05-30, USE-994): Because of the cast above, we have to
      // re-cast to the nullable type. I tried a few different configurations and
      // this seems to be the least hacky way to have typescript represent the
      // actual type
    }) as ErrorDetails | null;
  }

  if (!errorDetails) {
    errorDetails = DEFAULT_ERROR_MESSAGE_CONFIG;
  }

  if (errorDetails.isFullPage) {
    return {
      type: "fullPageModal",
      errorDetails,
      errorKey,
      endpointName: config.action.meta?.arg?.endpointName,
    };
  }

  // Note (Evan, 2024-01-19): Suppress the toast in a few specific scenarios:
  const suppressToast = Boolean(
    // billing plan errors are already displayed in the billing plan modal
    errorKey?.startsWith("billingPlan") ||
      // "path already in use" errors are already displayed in the page data modal
      errorKey === "pathAlreadyInUse" ||
      // RTKQ throws a "ConditionError" to bail out of queries that are already in progress,
      // this is internal to RTKQ so we don't want to alert the user
      // (see https://github.com/reduxjs/redux-toolkit/issues/2501)
      config.action.meta?.condition,
  );

  if (!suppressToast) {
    return {
      type: "toast",
      errorDetails,
      errorKey,
      endpointName: config.action.meta?.arg?.endpointName,
    };
  }
  return {
    type: "none",
    errorKey,
    endpointName: config.action.meta?.arg?.endpointName,
  };
};
